/**
 * State + ARIA wiring for the button that opens a `Menu`.
 *
 * Click toggles; ArrowDown / ArrowUp on the trigger open the menu with focus
 * on the first / last item, as in a native menu button.
 *
 * `hover: true` (the top-bar groups) also opens the menu when a mouse
 * pointer enters the trigger – without moving focus, so a hover never takes
 * the cursor out of a text field. A hover-opened menu closes once the
 * pointer has left trigger and panel for `HOVER_CLOSE_MS`; a click on its
 * trigger pins it open instead of toggling it shut. Touch and keyboard keep
 * the click/key behaviour. Spread `panelProps` onto the `Menu`.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";

export type MenuFocusEdge = "first" | "last";
/** Which item gets focus on open; `"none"` leaves focus where it is. */
export type MenuFocus = MenuFocusEdge | "none";

/** Grace for the gap between trigger and panel, and for a shaky hand. */
export const HOVER_CLOSE_MS = 150;

// The open hover-mode menu. Opening another one closes it at once, so moving
// along the top bar never shows two menus during the grace period.
let closeOpenBarMenu: (() => void) | null = null;

export function useMenuTrigger({ hover = false }: { hover?: boolean } = {}) {
  const [open, setOpenState] = useState(false);
  const [edge, setEdge] = useState<MenuFocus>("first");
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();
  const openedByHover = useRef(false);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const closeSelf = useRef<() => void>(() => {});

  const setOpen = useCallback(
    (next: boolean, focus: MenuFocus = "first") => {
      cancelClose();
      if (!next) openedByHover.current = false;
      if (hover) {
        if (next) {
          if (closeOpenBarMenu && closeOpenBarMenu !== closeSelf.current) closeOpenBarMenu();
          closeOpenBarMenu = closeSelf.current;
        } else if (closeOpenBarMenu === closeSelf.current) {
          closeOpenBarMenu = null;
        }
      }
      setEdge(focus);
      setOpenState(next);
    },
    [cancelClose, hover],
  );

  useEffect(() => {
    closeSelf.current = () => setOpen(false);
  }, [setOpen]);

  useEffect(
    () => () => {
      cancelClose();
      if (closeOpenBarMenu === closeSelf.current) closeOpenBarMenu = null;
    },
    [cancelClose],
  );

  const scheduleClose = useCallback(() => {
    // Only a hover-opened menu follows the pointer; a pinned one stays.
    if (!openedByHover.current) return;
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), HOVER_CLOSE_MS);
  }, [cancelClose, setOpen]);

  const focusFirstItem = () => {
    document
      .getElementById(menuId)
      ?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')
      ?.focus({ preventScroll: true });
  };

  const triggerProps = {
    ref: triggerRef,
    type: "button" as const,
    "aria-haspopup": "menu" as const,
    "aria-expanded": open,
    "aria-controls": open ? menuId : undefined,
    onClick: () => {
      if (open && openedByHover.current) {
        openedByHover.current = false; // pin it
        cancelClose();
        return;
      }
      setOpen(!open);
    },
    onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (open) {
          // Opened by hover without focus: the keys take it over.
          openedByHover.current = false;
          cancelClose();
          focusFirstItem();
          return;
        }
        setOpen(true, e.key === "ArrowUp" ? "last" : "first");
      }
    },
    ...(hover
      ? {
          onPointerEnter: (e: PointerEvent<HTMLButtonElement>) => {
            if (e.pointerType !== "mouse") return;
            cancelClose();
            if (open) return;
            setOpen(true, "none");
            openedByHover.current = true;
          },
          onPointerLeave: scheduleClose,
        }
      : {}),
  };

  const panelProps = hover ? { onPointerEnter: cancelClose, onPointerLeave: scheduleClose } : {};

  return { open, setOpen, edge, triggerRef, triggerProps, panelProps, menuId };
}
